import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { environment } from "../../environments/environment";
import { DmsAuthService } from "../services/dms-auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isLoggingOut = false; // Flag to prevent recursive logout calls

  constructor(
    private router: Router,
    private messageService: MessageService,
    private http: HttpClient,
    private dmsAuthService: DmsAuthService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Get token from localStorage
    const token = localStorage.getItem("token");

    // Skip adding token for homedashboard endpoint
    const url = request.url || "";
    const isHomedashboardEndpoint = url.includes("homedashboard");
    const isDmsEndpoint = url.includes("backenddms.pure.bi");

    // Clone the request and add the Authorization header
    let clonedRequest = request;

    if (isDmsEndpoint) {
      // For DMS endpoints, only use x-api-key (no Bearer token needed for external API)
      clonedRequest = request.clone({
        headers: request.headers.set(
          "x-api-key",
          "PUREBI-MOBL-PROD-21121989-X8F9A7C3"
        ),
      });
      console.log(
        "[AuthInterceptor] DMS endpoint detected, adding x-api-key header only"
      );
    } else if (token && !isHomedashboardEndpoint) {
      // For other endpoints, use the main app token
      clonedRequest = request.clone({
        headers: request.headers.set("Authorization", `Bearer ${token}`),
      });
    }

    // Handle the request and catch errors
    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if it's a 401 Unauthorized error
        if (error.status === 401) {
          console.log("🔴 401 Error detected!", {
            url: error.url || request.url,
            status: error.status,
            isLoggingOut: this.isLoggingOut,
          });

          // Skip logout for login/signup/forgot password endpoints (these are expected to return 401 on failure)
          const url = error.url || request.url || "";
          const isAuthEndpoint =
            url.includes("/login") ||
            url.includes("/signup") ||
            url.includes("/forgotpass") ||
            url.includes("/resetPassword") ||
            url.includes("/confirmPassword");

          console.log("🔍 Checking conditions:", {
            url,
            isAuthEndpoint,
            isLoggingOut: this.isLoggingOut,
            willLogout: !isAuthEndpoint && !this.isLoggingOut,
          });

          // Only handle logout if it's NOT an authentication endpoint and NOT already logging out
          // (login/signup endpoints can return 401 on invalid credentials, which is normal)
          // The isLoggingOut flag prevents recursive calls when the logout API itself returns 401
          if (!isAuthEndpoint && !this.isLoggingOut) {
            console.log(
              "✅ 401 Unauthorized detected, initiating logout process..."
            );

            // Set flag to prevent recursive calls
            this.isLoggingOut = true;

            // Call logout API to notify backend (fire-and-forget, don't wait for response)
            // IMPORTANT: Call this BEFORE clearing localStorage so we have user info and token
            this.callLogoutAPI();

            // Clear ALL localStorage and sessionStorage completely
            console.log('🧹 401 Interceptor: Clearing all localStorage and sessionStorage...');
            try {
              localStorage.clear();
              sessionStorage.clear();
              console.log('✅ All storage cleared on 401 unauthorized');
            } catch (error) {
              console.error('❌ Error clearing storage:', error);
              // Fallback to individual removal
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              localStorage.removeItem("user-info");
              localStorage.removeItem("sites-info");
              localStorage.removeItem("completeModule");
              localStorage.removeItem("corporateModule");
              localStorage.removeItem("labModule");
              localStorage.removeItem("editModule");
              localStorage.removeItem("GROUP");
              sessionStorage.removeItem("token");
              sessionStorage.removeItem("user");
              sessionStorage.removeItem("user-info");
              sessionStorage.removeItem("sites-info");
              sessionStorage.removeItem("current-site");
              sessionStorage.removeItem("visitedUserManagementSites");
              sessionStorage.removeItem("admin_id");
              this.clearNotificationStorage();
              localStorage.removeItem("notification_theme");
              localStorage.removeItem("uploadedpath");
            }

            // Show session expired message
            this.messageService.add({
              severity: "error",
              summary: "Session expired. Please login again.",
              detail:
                "Your session has expired. Please login again to continue.",
            });

            // Redirect to login page
            this.router.navigate(["/login"]);
          }
        }

        // Re-throw the error so it can be handled by the calling code if needed
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if we're within the grace period after login
   * Returns true if we should skip logout API calls
   */
  private isWithinLoginGracePeriod(): boolean {
    const loginTimestamp = sessionStorage.getItem("loginTimestamp");
    if (!loginTimestamp) {
      return false; // No login timestamp, not in grace period
    }

    const loginTime = parseInt(loginTimestamp, 10);
    const now = Date.now();
    const timeSinceLogin = now - loginTime;
    const GRACE_PERIOD = 5000; // 5 seconds grace period after login

    if (timeSinceLogin < GRACE_PERIOD) {
      console.log(
        `⏱️ Within login grace period (${Math.round(
          timeSinceLogin
        )}ms < ${GRACE_PERIOD}ms), skipping logout API call`
      );
      return true;
    }

    // Grace period expired, clear the timestamp
    sessionStorage.removeItem("loginTimestamp");
    return false;
  }

  /**
   * Call logout API to notify backend about session invalidation
   * This is fire-and-forget - we don't wait for the response
   */
  private callLogoutAPI(): void {
    try {
      // Skip logout API call if we're within the grace period after login
      // This prevents false logout calls during navigation after login
      if (this.isWithinLoginGracePeriod()) {
        console.log("⏱️ Skipping logout API call - within login grace period");
        return;
      }

      console.log("🚀 callLogoutAPI: Starting logout API call...");

      // Get token before making the call (it will be cleared after this)
      const token = localStorage.getItem("token");
      console.log(
        "🔑 Token found:",
        !!token,
        token ? token.substring(0, 20) + "..." : "none"
      );

      if (!token) {
        console.warn(
          "⚠️ callLogoutAPI: No token found, skipping logout API call"
        );
        return;
      }

      // Use nodeUrl1 (http://localhost:4000/) + auth/logout endpoint
      const logoutUrl = `${environment.nodeUrl1}auth/logout`;
      console.log("🌐 callLogoutAPI: Calling logout API:", logoutUrl);
      console.log("📤 Request details:", {
        method: "POST",
        url: logoutUrl,
        headers: {
          Authorization: `Bearer ${token.substring(0, 20)}...`,
          "Content-Type": "application/json",
        },
      });

      // Use fetch API with keepalive to ensure it completes even if page navigates away
      // Even if it fails (e.g., also returns 401), we still proceed with cleanup
      console.log("📡 Making fetch request to:", logoutUrl);

      fetch(logoutUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        keepalive: true, // Ensures request completes even if page unloads
        // No body needed!
      })
        .then((response) => {
          console.log("✅ Logout API called successfully!", {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            url: response.url,
          });
          return response;
        })
        .catch((err) => {
          // Silently handle errors - we're already handling session expiration
          console.error(
            "⚠️ Logout API call failed (expected if session expired):",
            err
          );
        });

      console.log("📤 Fetch request initiated (fire-and-forget)");
    } catch (error) {
      // If we can't make the call, that's okay
      // We still proceed with clearing data and redirecting
      console.error("❌ Error calling logout API:", error);
    }
  }

  private clearNotificationStorage(): void {
    try {
      // Clear all app_notifications_* keys from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("app_notifications_")) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.log(`401 Logout cleanup: Cleared notification key: ${key}`);
      });

      if (keysToRemove.length > 0) {
        console.log(
          `401 Logout cleanup: Cleared ${keysToRemove.length} notification keys`
        );
      }
    } catch (e) {
      console.error(
        "Error clearing notification storage during 401 logout:",
        e
      );
    }
  }
}
